import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";

const GBA_LOGO = "https://customer-assets.emergentagent.com/job_prospect-tracker-24/artifacts/aek653mu_GBA%20LOGO%20PNG.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back! Let's close some deals!");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fade-in">
          <img src={GBA_LOGO} alt="GBA" className="h-24 mx-auto mb-6" />
          <h1 className="font-heading text-4xl font-bold text-white tracking-tight uppercase">
            Sales Acceleration
          </h1>
          <p className="text-slate-400 mt-2">
            Master follow-ups, handle objections, close more deals
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up">
          <h2 className="font-heading text-2xl font-semibold text-white mb-6">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-400 hover:text-gba-green"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 text-lg uppercase tracking-wide shadow-glow btn-press"
              data-testid="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Sign In <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="text-gba-green hover:text-gba-green-hover font-medium"
                data-testid="register-link"
              >
                Create one
              </Link>
            </p>
          </div>
        </div>

        {/* Motivational Quote */}
        <p className="text-center text-slate-500 text-sm mt-8 italic">
          "The fortune is in the follow-up."
        </p>
      </div>
    </div>
  );
};

export default Login;
